// @purpose MOTION
// Motion boot (entry "bench", tiny; docs/AUDIT_V3.md X1-X5). Everything here is progressive: every page it
// touches is complete and readable without it. Motion "none" / "reduced": benches answer with still frames
// (never a moving one), reveals and draw-ins don't play (their end state is what the page already shows).
//  * [data-bench]: the "same load, different materials" benches (home demo, /materials/ shelf, pair card):
//    ./bench.js + the materials' plugins load lazily, after load + idle, once the bench nears the viewport.
//  * [data-reveal]: the result reveal (X2): test chamber -> the rig retracts -> the material resolves ->
//    the finish layer -> the five-moment rail lights up. CSS does the motion (results.css .mreveal); this
//    only starts it: full motion, first view in this tab session (sessionStorage, in try/catch: no storage
//    = no replay risk = no reveal), skippable by a tap / Skip / Escape, ~2.2 s.
//  * [data-draw="ready"]: one-shot draw-ins (half-life charts, result bars + rings, the Shiny bounce) wait
//    off-screen ("wait") and play when 35 % visible ("go"). Without this script they are simply drawn.
// The pair card hides its difference lines while the shared load replays (bench:play), then lets them in.
import { track } from "../track.js";
import { motionMode, onMotionChange } from "../scene/motion_mode.js";

const benches = new Map();
const mod = () => import("./bench.js");
const REVEAL_MS = 2250;
const PAIR_MS = 1900;

function afterLoadIdle(fn) {
  const idle = (cb) => (window.requestIdleCallback ? requestIdleCallback(cb, { timeout: 2000 }) : setTimeout(cb, 300));
  if (document.readyState === "complete") idle(fn);
  else window.addEventListener("load", () => idle(fn), { once: true });
}

function near(el, fn, margin = "200px", threshold = 0) {
  if (!("IntersectionObserver" in window)) return fn();
  const io = new IntersectionObserver(
    (es) => {
      if (es.some((e) => e.isIntersecting)) {
        io.disconnect();
        fn();
      }
    },
    { rootMargin: margin, threshold },
  );
  io.observe(el);
}

function bench(root) {
  if (benches.has(root)) return;
  benches.set(root, null);
  afterLoadIdle(() =>
    near(root, async () => {
      if (!root.isConnected) return;
      // motion off and calm both get still frames (the stepper): the bench never moves, it still answers
      const motion = motionMode() === "full" ? "full" : "reduced";
      try {
        benches.set(root, await (await mod()).mountBench(root, { motion }));
      } catch {
        benches.delete(root); // static posters stay: nothing is lost
      }
    }),
  );
  const card = root.closest("[data-pair-card]");
  let t = 0;
  root.addEventListener("bench:play", (e) => {
    if (!card || e.detail.reduced) return;
    card.classList.add("is-diverging");
    clearTimeout(t);
    t = setTimeout(() => card.classList.remove("is-diverging"), PAIR_MS);
  });
}

function reveal(el) {
  if (el.dataset.revealOn) return;
  el.dataset.revealOn = "1";
  let fresh = false;
  try {
    const key = `calm.reveal.${el.dataset.reveal}`;
    fresh = sessionStorage.getItem(key) !== "1";
    sessionStorage.setItem(key, "1");
  } catch {
    fresh = false;
  }
  if (!fresh || motionMode() !== "full") return;
  el.classList.add("is-play");
  const skip = el.querySelector("[data-reveal-skip]");
  if (skip) skip.hidden = false;
  let t = 0;
  const done = (skipped) => {
    clearTimeout(t);
    if (!el.classList.contains("is-play")) return;
    el.classList.remove("is-play");
    for (const d of el.querySelectorAll("[data-draw]")) d.dataset.draw = "go"; // e.g. the Shiny bounce, after the reveal
    if (skip) skip.hidden = true;
    document.removeEventListener("keydown", esc);
    if (skipped) track("specimen_interacted", { material: el.dataset.material || "all", action: "skip", state: "calm", place: "reveal" });
  };
  const esc = (ev) => ev.key === "Escape" && done(true);
  t = setTimeout(() => done(false), REVEAL_MS);
  el.addEventListener("click", () => done(true), { once: true });
  document.addEventListener("keydown", esc);
}

let drawIO = null;
function draw(el) {
  if (motionMode() !== "full" || !("IntersectionObserver" in window)) return;
  if (el.closest(".mreveal.is-play")) return void (el.dataset.draw = "wait"); // the reveal plays it when it ends
  drawIO ||= new IntersectionObserver(
    (es) => {
      for (const e of es) {
        if (!e.isIntersecting) continue;
        e.target.dataset.draw = "go";
        drawIO.unobserve(e.target);
      }
    },
    { threshold: 0.35 },
  );
  el.dataset.draw = "wait";
  drawIO.observe(el);
}

function scan() {
  for (const [root, b] of benches) {
    if (root.isConnected) continue;
    b?.dispose();
    benches.delete(root);
  }
  for (const el of document.querySelectorAll("[data-bench]")) bench(el);
  if (motionMode() === "none") return;
  for (const el of document.querySelectorAll("[data-reveal]")) reveal(el);
  for (const el of document.querySelectorAll('[data-draw="ready"]')) draw(el);
}

onMotionChange((mode) => {
  for (const [root, b] of benches) {
    b?.dispose();
    benches.delete(root);
  }
  for (const el of document.querySelectorAll('[data-draw="wait"]')) el.dataset.draw = "ready";
  scan();
});

scan();
document.addEventListener("htmx:afterSettle", scan);
